<template>
  <div id="app" class="h-dvh overflow-hidden" :class="{ 'app-webview': isWebView }">
    <component :is="layout">
      <RouterView v-slot="{ Component }">
        <template v-if="Component">
          <!-- No <Transition> or <Suspense> around the routed page, on purpose.
               Suspense was dead weight (no page suspends — they fetch in onMounted)
               and crashed with an insertBefore NotFoundError on fast route switches.
               Transition mode="out-in" + KeepAlive gets stuck rendering its empty
               placeholder when a navigation interrupts a page that is mid-render
               (long-standing Vue issue) — the portal then shows a blank page.
               The page-enter CSS keyframe below reproduces the enter animation:
               it replays on every DOM insertion, including KeepAlive re-activations. -->
          <KeepAlive>
            <component :is="Component" :class="transitionName && 'page-enter'" />
          </KeepAlive>
        </template>
      </RouterView>
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, shallowRef } from 'vue'
import { useStore } from 'vuex'

import layouts from '@/layouts/layouts'
import router from '@/router'

const store = useStore()
const isWebView = computed<boolean>(() => store.getters['users/isWebView'])

const layout = shallowRef('div')
const transitionName = ref('route-transition')

router.beforeEach((to, from) => {
  transitionName.value = to.meta.noTransition || from.meta.noTransition ? '' : 'route-transition'
})

router.afterEach((to) => {
  layout.value = layouts[to.meta.layout as string] || 'div'
})

provide('app:layout', layout)
</script>

<style>
/*
 * Plain CSS keyframe instead of a <Transition> (see template comment). Not
 * scoped: the class is applied to routed page roots via attr fallthrough, and
 * scoped data attributes don't reach them reliably through KeepAlive.
 */
.page-enter {
  animation: route-enter 0.18s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes route-enter {
  from {
    opacity: 0;
    transform: translate3d(0, 0.5rem, 0) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0) scale(1);
  }
}
</style>

<style>
/* Native-feel (no text selection / long-press callout) only when hosted in the
   mobile-app WebView — desktop browser users keep normal select & copy. */
#app.app-webview {
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}

/* Keep editable fields selectable so typing/caret placement still works. */
input,
textarea,
[contenteditable='true'] {
  -webkit-user-select: text;
  user-select: text;
}
</style>
